import React from 'react';
import I18n from 'i18n-js';
import classNames from 'classnames';
import { Location } from 'history';

import CoreLink from './core_link';
import { getCategory, getSubcategory } from '../csp_category_helper';
import { CSP } from '../rql';
import DynamicImage from './dynamic_image';
import { MParticleEvent } from '../elog/mparticle_types';
import {
  BrandMetaData,
  NameMetaData,
  DescriptionMetaData,
  ImageMetaData,
  ReviewMetaData,
  UrlMetaData,
} from '../cms/lib/metadata_helper';

interface IProps {
  csp?: CSP;
  position?: number;
  hideMetadata?: boolean;
  renderAsHorizontal?: boolean;
  location?: Location;
  destinationPath?: string;
  clickEvent?: MParticleEvent;
  children?: JSX.Element;
  hideFinishes?: boolean;
  header?: string | JSX.Element | React.ReactNode;
}

function Category({ csp, location }) {
  if (!csp.categories) { return null; }

  const isFilteringCategory = !!location?.query?.product_type;
  const isFilteringSubcategory = !!location?.query?.category;
  const category = getCategory(csp);
  const subCategory = getSubcategory(csp);

  return (
    <div className="csp-square-card__category">
      {!isFilteringCategory && category?.name &&
        <span>
          {category.name}
        </span>
      }
      {!isFilteringSubcategory && subCategory?.name &&
        <span>
          {subCategory.name}
        </span>
      }
    </div>
  );
}

function Title({ csp, location, showMetadata }) {
  return (
    <div
      className="csp-square-card__title"
    >
      <div className="size-90 weight-normal">
        {csp.brand?.name}
      </div>
      <h3
        itemProp="model"
        className="weight-bold"
      >
        {csp.title.replace(csp.brand?.name, '')}
      </h3>
      <Category csp={csp} location={location} />
      {showMetadata &&
        <div>
          <BrandMetaData csp={csp} />
          <NameMetaData csp={csp} />
          <DescriptionMetaData csp={csp} />
          <ImageMetaData csp={csp} />
          <ReviewMetaData csp={csp} />
          <UrlMetaData csp={csp} />
        </div>
      }
    </div>
  );
}

interface ImageAndFinishIndicatorProps {
  csp: CSP;
  hideFinishes?: boolean;
}

export function ImageAndFinishIndicator({ csp, hideFinishes }: ImageAndFinishIndicatorProps) {
  return (
    <>
      <DynamicImage
        src={csp.image?.source}
      />
      {!hideFinishes && csp.finishes?.length > 1 &&
        <div className="csp-square-card__finish-indicator">
          {I18n.t('discovery.sellFlow.tiles.multipleFinishes')}
        </div>
      }
    </>
  );
}

function SquareCardInner(props: IProps) {
  return (
    <>
      { props.header && !props.renderAsHorizontal &&
        <div className="csp-square-card__header">
          {props.header}
        </div>
      }
      <div className="csp-square-card__main">
        <ImageAndFinishIndicator csp={props.csp} hideFinishes={props.hideFinishes} />
        {!props.renderAsHorizontal &&
          <Title
            csp={props.csp}
            location={props.location}
            showMetadata={!props.hideMetadata}
          />
        }
      </div>
      <div
        className="csp-square-card__details"
      >
        {props.renderAsHorizontal &&
          <Title
            csp={props.csp}
            location={props.location}
            showMetadata={!props.hideMetadata}
          />
        }
        {props.children}
      </div>
    </>
  );
}

export function SquareCard(props: IProps) {
  const getItemMetadata = () => {
    if (props.hideMetadata) return;

    return {
      itemScope: true,
      itemType: 'http://schema.org/Product',
    };
  };

  const classes = classNames(
    'csp-square-card',
    { 'csp-square-card--horizontal': props.renderAsHorizontal },
  );

  return (
    <article
      className={classes}
      {...getItemMetadata()}
    >
      {props.destinationPath ?
        <CoreLink
          className="csp-square-card__inner"
          to={props.destinationPath}
          clickEvent={props.clickEvent}
          entityId={props.csp.id}
          position={props.position}
        >
          <SquareCardInner {...props} />
        </CoreLink>
        :
        <div className="csp-square-card__inner">
          <SquareCardInner {...props} />
        </div>
      }
    </article>
  );
}
